/**
 * Accounting Service
 * Frontend service for accounting module API calls
 */

import { apiClient, APIResponse } from './client';
import {
  AccountingConfig,
  AccountingConfigUpdate,
  Account,
  AccountCreate,
  AccountUpdate,
  CostCentre,
  CostCentreCreate,
  CostCentreUpdate,
  AccountingRule,
  AccountingRuleCreate,
  AccountingRuleUpdate,
  ValuationUpload,
  ValuationUploadResult,
  TradeWithValuation,
  AccountingEvent,
  AccountingEntry,
  GenerationRequest,
  GenerationResult,
  GenerationSchedule,
  AccountingBatch,
  ExportSettings,
} from '../../types/accounting';

class AccountingService {
  // ============================================
  // Configuration Methods
  // ============================================

  /**
   * Get accounting configuration for a client
   * Returns stored configuration or defaults if none exists
   */
  async getConfig(clientId: string): Promise<APIResponse<AccountingConfig>> {
    return apiClient.get<AccountingConfig>(`/api/v1/accounting/${clientId}/config`);
  }

  /**
   * Update accounting configuration for a client
   * Requires manage_accounting permission
   */
  async updateConfig(
    clientId: string,
    config: AccountingConfigUpdate
  ): Promise<APIResponse<AccountingConfig>> {
    return apiClient.put<AccountingConfig>(
      `/api/v1/accounting/${clientId}/config`,
      config
    );
  }

  /**
   * Reset export settings to defaults
   */
  async resetExportSettings(clientId: string): Promise<APIResponse<AccountingConfig>> {
    return apiClient.post<AccountingConfig>(
      `/api/v1/accounting/${clientId}/export-settings/reset`
    );
  }

  // ============================================
  // Generation Schedule Methods
  // ============================================

  /**
   * Get generation schedule configuration for a client
   */
  async getGenerationSchedule(clientId: string): Promise<APIResponse<GenerationSchedule>> {
    return apiClient.get<GenerationSchedule>(
      `/api/v1/accounting/${clientId}/generation-schedule`
    );
  }

  /**
   * Save generation schedule configuration for a client
   * Requires manage_accounting permission
   */
  async saveGenerationSchedule(
    clientId: string,
    schedule: GenerationSchedule
  ): Promise<APIResponse<GenerationSchedule>> {
    return apiClient.put<GenerationSchedule>(
      `/api/v1/accounting/${clientId}/generation-schedule`,
      schedule
    );
  }

  // ============================================
  // Chart of Accounts Methods
  // ============================================

  /**
   * Get all accounts for a client
   */
  async getAccounts(
    clientId: string,
    params?: {
      type?: string;
      search?: string;
      includeInactive?: boolean;
    }
  ): Promise<APIResponse<Account[]>> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.includeInactive) queryParams.append('includeInactive', 'true');

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/accounts${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<Account[]>(url);
  }

  /**
   * Get a single account by ID
   */
  async getAccount(clientId: string, accountId: string): Promise<APIResponse<Account>> {
    return apiClient.get<Account>(`/api/v1/accounting/${clientId}/accounts/${accountId}`);
  }

  /**
   * Create a new account
   * Requires manage_accounting permission
   */
  async createAccount(
    clientId: string,
    account: AccountCreate
  ): Promise<APIResponse<Account>> {
    return apiClient.post<Account>(
      `/api/v1/accounting/${clientId}/accounts`,
      account
    );
  }

  /**
   * Update an existing account
   * Requires manage_accounting permission
   */
  async updateAccount(
    clientId: string,
    accountId: string,
    account: AccountUpdate
  ): Promise<APIResponse<Account>> {
    return apiClient.put<Account>(
      `/api/v1/accounting/${clientId}/accounts/${accountId}`,
      account
    );
  }

  /**
   * Delete (deactivate) an account
   * Requires manage_accounting permission
   */
  async deleteAccount(clientId: string, accountId: string): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/api/v1/accounting/${clientId}/accounts/${accountId}`);
  }

  // ============================================
  // Cost Centre Methods
  // ============================================

  /**
   * Get all cost centres for a client
   */
  async getCostCentres(
    clientId: string,
    params?: {
      search?: string;
      includeInactive?: boolean;
    }
  ): Promise<APIResponse<CostCentre[]>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.includeInactive) queryParams.append('includeInactive', 'true');

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/cost-centres${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<CostCentre[]>(url);
  }

  /**
   * Get a single cost centre by ID
   */
  async getCostCentre(clientId: string, costCentreId: string): Promise<APIResponse<CostCentre>> {
    return apiClient.get<CostCentre>(`/api/v1/accounting/${clientId}/cost-centres/${costCentreId}`);
  }

  /**
   * Create a new cost centre
   */
  async createCostCentre(
    clientId: string,
    data: CostCentreCreate
  ): Promise<APIResponse<CostCentre>> {
    return apiClient.post<CostCentre>(
      `/api/v1/accounting/${clientId}/cost-centres`,
      data
    );
  }

  /**
   * Update an existing cost centre
   */
  async updateCostCentre(
    clientId: string,
    costCentreId: string,
    data: CostCentreUpdate
  ): Promise<APIResponse<CostCentre>> {
    return apiClient.put<CostCentre>(
      `/api/v1/accounting/${clientId}/cost-centres/${costCentreId}`,
      data
    );
  }

  /**
   * Delete (deactivate) a cost centre
   */
  async deleteCostCentre(clientId: string, costCentreId: string): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/api/v1/accounting/${clientId}/cost-centres/${costCentreId}`);
  }

  // ============================================
  // Accounting Rules Methods (Phase 3)
  // ============================================

  /**
   * Get all rules for a client
   */
  async getRules(
    clientId: string,
    params?: {
      concept?: string;
      includeInactive?: boolean;
    }
  ): Promise<APIResponse<AccountingRule[]>> {
    const queryParams = new URLSearchParams();
    if (params?.concept) queryParams.append('concept', params.concept);
    if (params?.includeInactive) queryParams.append('includeInactive', 'true');

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/rules${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<AccountingRule[]>(url);
  }

  /**
   * Get a single rule by ID
   */
  async getRule(clientId: string, ruleId: string): Promise<APIResponse<AccountingRule>> {
    return apiClient.get<AccountingRule>(`/api/v1/accounting/${clientId}/rules/${ruleId}`);
  }

  /**
   * Create a new rule
   */
  async createRule(
    clientId: string,
    rule: AccountingRuleCreate
  ): Promise<APIResponse<AccountingRule>> {
    return apiClient.post<AccountingRule>(
      `/api/v1/accounting/${clientId}/rules`,
      rule
    );
  }

  /**
   * Update an existing rule
   */
  async updateRule(
    clientId: string,
    ruleId: string,
    rule: AccountingRuleUpdate
  ): Promise<APIResponse<AccountingRule>> {
    return apiClient.put<AccountingRule>(
      `/api/v1/accounting/${clientId}/rules/${ruleId}`,
      rule
    );
  }

  /**
   * Delete (deactivate) a rule
   */
  async deleteRule(clientId: string, ruleId: string): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/api/v1/accounting/${clientId}/rules/${ruleId}`);
  }

  /**
   * Upload rule splits Excel file (Iansa-only)
   */
  async uploadRuleSplits(
    clientId: string,
    file: File
  ): Promise<APIResponse<{
    updated: { rule: string; splits: { side: string; account: string; percentage: number }[] }[];
    notFound: string[];
    errors: string[];
    summary: { rulesUpdated: number; rulesNotFound: number; errors: number };
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.postFormData(
      `/api/v1/accounting/${clientId}/upload-rule-splits`,
      formData
    );
  }

  // ============================================
  // Valuation Methods (Phase 4)
  // ============================================

  /**
   * Upload valuation Excel file
   */
  async uploadValuations(
    clientId: string,
    file: File
  ): Promise<APIResponse<ValuationUploadResult>> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.postFormData<ValuationUploadResult>(
      `/api/v1/accounting/${clientId}/valuations/upload`,
      formData
    );
  }

  /**
   * Get recent valuation uploads (upload log)
   */
  async getValuationUploads(
    clientId: string,
    limit?: number
  ): Promise<APIResponse<ValuationUpload[]>> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', String(limit));

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/valuations/uploads${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<ValuationUpload[]>(url);
  }

  /**
   * Get latest valuations (trades with their current/previous valuation)
   */
  async getLatestValuations(clientId: string): Promise<APIResponse<TradeWithValuation[]>> {
    return apiClient.get<TradeWithValuation[]>(`/api/v1/accounting/${clientId}/valuations/latest`);
  }

  /**
   * Pre-delete check: returns whether the upload has dependent accounting events
   */
  async checkDeleteValuationUpload(
    clientId: string,
    uploadId: string
  ): Promise<APIResponse<{ uploadId: string; hasAccountingEvents: boolean; accountingEventCount: number }>> {
    return apiClient.get<{ uploadId: string; hasAccountingEvents: boolean; accountingEventCount: number }>(
      `/api/v1/accounting/${clientId}/valuations/uploads/${uploadId}/check-delete`
    );
  }

  /**
   * Soft-delete a specific valuation upload and clear its valuations from trades
   */
  async deleteValuationUpload(
    clientId: string,
    uploadId: string
  ): Promise<APIResponse<{ uploadId: string; tradesUpdated: number; accountingEventsAffected: number }>> {
    return apiClient.delete<{ uploadId: string; tradesUpdated: number; accountingEventsAffected: number }>(
      `/api/v1/accounting/${clientId}/valuations/uploads/${uploadId}`
    );
  }

  // ============================================
  // Generation Methods (Phase 6-7)
  // ============================================

  /**
   * Generate accounting events and entries
   */
  async generate(
    clientId: string,
    request: GenerationRequest
  ): Promise<APIResponse<GenerationResult>> {
    return apiClient.post<GenerationResult>(
      `/api/v1/accounting/${clientId}/generate`,
      request
    );
  }

  /**
   * Get accounting events
   */
  async getEvents(
    clientId: string,
    params?: {
      concept?: string;
      dateFrom?: string;
      dateTo?: string;
      processed?: boolean;
      batchId?: string;
    }
  ): Promise<APIResponse<AccountingEvent[]>> {
    const queryParams = new URLSearchParams();
    if (params?.concept) queryParams.append('concept', params.concept);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.processed !== undefined) queryParams.append('processed', String(params.processed));
    if (params?.batchId) queryParams.append('batchId', params.batchId);

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/events${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<AccountingEvent[]>(url);
  }

  /**
   * Generate accounting entries from unprocessed events
   */
  async generateEntries(
    clientId: string,
    params?: {
      batchId?: string;
      concepts?: string[];
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<APIResponse<{
    success: boolean;
    batchId: string;
    entriesGenerated: number;
    eventsProcessed: number;
    errors: Array<{ eventId?: string; tradeNumber?: string; error: string }>;
    summary: { byConceptEntries?: Record<string, number> };
  }>> {
    return apiClient.post(
      `/api/v1/accounting/${clientId}/generate-entries`,
      params || {}
    );
  }

  /**
   * Get accounting entries
   */
  async getEntries(
    clientId: string,
    params?: {
      concept?: string;
      accountNumber?: string;
      tradeNumber?: string;
      dateFrom?: string;
      dateTo?: string;
      batchId?: string;
    }
  ): Promise<APIResponse<AccountingEntry[]>> {
    const queryParams = new URLSearchParams();
    if (params?.concept) queryParams.append('concept', params.concept);
    if (params?.accountNumber) queryParams.append('accountNumber', params.accountNumber);
    if (params?.tradeNumber) queryParams.append('tradeNumber', params.tradeNumber);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.batchId) queryParams.append('batchId', params.batchId);

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/entries${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<AccountingEntry[]>(url);
  }

  /**
   * Get recent accounting generation batches
   */
  async getBatches(
    clientId: string,
    limit?: number,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<APIResponse<AccountingBatch[]>> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', String(limit));
    if (dateFrom) queryParams.append('dateFrom', dateFrom);
    if (dateTo) queryParams.append('dateTo', dateTo);

    const queryString = queryParams.toString();
    const url = `/api/v1/accounting/${clientId}/batches${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<AccountingBatch[]>(url);
  }

  /**
   * Delete a batch and all its associated events
   */
  async deleteBatch(
    clientId: string,
    batchId: string
  ): Promise<APIResponse<{ batchId: string; eventsDeleted: number }>> {
    return apiClient.delete<{ batchId: string; eventsDeleted: number }>(
      `/api/v1/accounting/${clientId}/batches/${batchId}`
    );
  }

  /**
   * Delete all accounting entries (demo mode only)
   */
  async deleteAllEntries(
    clientId: string
  ): Promise<APIResponse<{ entriesDeleted: number; batchesDeleted: number; eventsUnmarked: number }>> {
    return apiClient.delete<{ entriesDeleted: number; batchesDeleted: number; eventsUnmarked: number }>(
      `/api/v1/accounting/${clientId}/entries`
    );
  }
}

export const accountingService = new AccountingService();
